import React from 'react';
import {
  BrandThemeProvider,
  ComponentsLibrary,
  isV26,
} from '@volkswagen-onehub/vw-e-mobility-components';
import {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import {FeatureAppLoader, ReactFeatureApp} from '@feature-hub/react';
import {
  devConfig,
  fetchBffConfig,
  fetchNaturalLanguageBffConfig,
  getVwTrack,
  optionalVwServices,
  RequiredVwFeatureServices,
  requiredVwServices,
  VwFeatureServiceIds,
} from '@volkswagen-onehub/vw-e-mobility-feature-app-commons';
import {
  CommonFeatureServices,
  defineEmobilityNaturalLanguageChannelService,
  NaturalLanguageFeatureAppContentConfig,
  filterValidIds,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {SimulatorFeatureAppName} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {ThemeContext} from 'styled-components';
import {
  NaturalLanguageFeatureApp,
  NaturalLanguageFeatureAppConfig,
  NaturalLanguageFeatureAppDependencies,
  optionalServices,
  requiredExternals,
  requiredServices,
  customOptionalServices,
} from '@volkswagen-onehub/whitelabel-e-mobility-natural-language-feature-app';
import {
  createNaturalLanguageFeatureAppVariantTheme,
  NaturalLanguageFeatureAppWrapper,
} from './natural-language-theme';
import {FaContentServiceV1} from '@volkswagen-onehub/fa-content-service';
import {FeatureappServiceV1} from '@volkswagen-onehub/featureapp-service';
import {getLayerButtonFeatureAppConfiguration} from './utils/get-layer-button-feature-app-configuration';
import {getNaturalLanguageTrackingFunctions} from './utils/natural-language-tracking';
import {ApplicationRoot} from '@volkswagen-onehub/components-core';

if (!window.eMobilityTools) {
  window.eMobilityTools = {};
}
window.eMobilityTools.naturallanguage = {
  version: process.env.APP_VERSION || 'unknown',
};

const featureAppDefinition: FeatureAppDefinition<
  ReactFeatureApp,
  NaturalLanguageFeatureAppDependencies & RequiredVwFeatureServices,
  NaturalLanguageFeatureAppConfig
> = {
  dependencies: {
    externals: requiredExternals,
    featureServices: {
      ...requiredVwServices,
      ...requiredServices,
    },
  },
  optionalDependencies: {
    featureServices: {
      ...optionalVwServices,
      ...optionalServices,
      ...customOptionalServices,
      'featureapp-service': '^1.0.0',
    },
  },
  ownFeatureServiceDefinitions: [
    defineEmobilityNaturalLanguageChannelService(),
  ],
  create: function (env): ReactFeatureApp {
    const localeService =
      env.featureServices[CommonFeatureServices.localeService];
    const appDataContent = env.featureServices[
      VwFeatureServiceIds.contentService
    ].content as NaturalLanguageFeatureAppContentConfig;
    const featureAppService = env.featureServices[
      'featureapp-service'
    ] as FeatureappServiceV1;
    const logger = env.featureServices['s2:logger'];
    const customFeatureAppConfigurations =
      env.config?.customFeatureAppConfigurations;

    const appDataPromise = fetchNaturalLanguageBffConfig(
      {
        localeService,
        dataVersion:
          env.featureServices[VwFeatureServiceIds.dataVersionService],
      },
      env.featureServices[VwFeatureServiceIds.serviceConfigEndpointProvider],
      env.baseUrl
    );

    const carlines = filterValidIds(appDataContent.carlines);
    const rangeAppDataPromise = fetchBffConfig(
      SimulatorFeatureAppName.Range,
      {
        localeService,
        dataVersion:
          env.featureServices[VwFeatureServiceIds.dataVersionService],
      },
      env.featureServices[VwFeatureServiceIds.serviceConfigEndpointProvider],
      env.baseUrl,
      {
        ...(carlines.length > 0 && {carlines}),
      }
    );

    const vwTrack = getVwTrack(
      env.featureServices[VwFeatureServiceIds.trackingManager],
      localeService
    );
    const trackingFunctions = getNaturalLanguageTrackingFunctions(vwTrack);

    const loadingPromise = Promise.all([
      appDataPromise,
      rangeAppDataPromise,
    ]).then(() => undefined);

    const {
      featureAppConfiguration: layerButtonFeatureAppConfiguration,
      appDataContent: layerButtonAppDataContent,
      featureAppId: layerButtonFeatureAppId,
    } = getLayerButtonFeatureAppConfiguration(
      env.featureAppId,
      appDataContent,
      customFeatureAppConfigurations,
      featureAppService,
      logger
    );

    const beforeCreateLayerButton = (isNewBrandDesign?: boolean) => {
      return (
        env: FeatureAppEnvironment<
          FeatureServices & {['fa-content-service']?: FaContentServiceV1},
          unknown
        >
      ): void => {
        const {featureServices} = env;
        const {'fa-content-service': faContentService} = featureServices ?? {};

        if (!faContentService) {
          return;
        }

        faContentService.content = isNewBrandDesign
          ? {...layerButtonAppDataContent, isInverted: `${isNewBrandDesign}`}
          : layerButtonAppDataContent;
      };
    };

    return {
      loadingPromise,
      render() {
        return (
          <ApplicationRoot>
            <BrandThemeProvider theme="main">
              <ThemeContext.Consumer>
                {(brandTheme) => (
                  <NaturalLanguageFeatureAppWrapper>
                    <NaturalLanguageFeatureApp
                      componentsLibrary={ComponentsLibrary}
                      theme={createNaturalLanguageFeatureAppVariantTheme(
                        brandTheme
                      )}
                      featureAppEnvironment={env}
                      appDataPromise={appDataPromise}
                      appDataContent={appDataContent}
                      rangeAppDataPromise={rangeAppDataPromise}
                      trackingFunctions={trackingFunctions}
                      externallyRenderedDisclaimerKind="module-reference"
                      devConfig={devConfig}
                      channelId={layerButtonFeatureAppId}
                      isNewBrandDesign={isV26(brandTheme)}
                    >
                      <FeatureAppLoader
                        featureAppId={layerButtonFeatureAppId}
                        featureAppName={layerButtonFeatureAppConfiguration.name}
                        {...layerButtonFeatureAppConfiguration}
                        beforeCreate={beforeCreateLayerButton(
                          isV26(brandTheme)
                        )}
                      />
                    </NaturalLanguageFeatureApp>
                  </NaturalLanguageFeatureAppWrapper>
                )}
              </ThemeContext.Consumer>
            </BrandThemeProvider>
          </ApplicationRoot>
        );
      },
    };
  },
};

export default featureAppDefinition;
